import React, { FC } from 'react';
import { EngineSelectProps } from '@components/v2/EngineSelect/index';
import { Icon, Icons } from '@components/Icon';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import useAccessoryMonthlyPrices from '@hooks/useAccessoryPrices';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import PriceSuffix from '@components/PriceSuffix';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import useTranslations from '@hooks/useTranslations';
import { useCompareEngines } from '@hooks/useCompareEngines';
import { useUIDuck } from '@hooks/useUIDuck';
import { getTotalPriceObjByType } from '@utils/Price.utils';
import { IPriceV2Types } from '../../../services';
import Spinner from '@components/Spinner';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import Link from '@components/Link';
import {isBrandAC} from "../../../constants/main";

export const EngineSelectTemplate: FC<EngineSelectProps> = ({ className }) => {
    const { t } = useTranslations();
    const { getOptionMonthlyPrice } = useAccessoryMonthlyPrices();
    const { formatMoney } = useMoneyFormatter();
    const { motorizations } = useConfiguratorDuck();
    const { carDetails } = useCarDetailsDuck();
    const { isLoading } = useUIDuck();

    const { openEngineModal, onClickEngineComparator } = useCompareEngines();

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const isEngineComparatorEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_ENGINE_COMPARATOR);

    const engineCount = motorizations?.filter((motorisation) => motorisation.isAvailable)?.length || 0;

    return (
        <div className={className}>
            <span className="sectionTitle" data-testid="TESTING_CONFIG_ENGINE_SELECT_TITLE">
                {t('configurator.motorization.title', {
                    count: engineCount,
                })}
            </span>
            {isEngineComparatorEnabled && motorizations.length > 1 && (
                <Link
                    label={t('configurator.engineComparator.button')}
                    onClick={openEngineModal}
                    primary={!isBrandAC}
                    secondary={isBrandAC}
                    withArrowIcon
                />
            )}
            {isLoading && <Spinner size={36} border={2} color="black" />}
            <div id="skip-to-content" className={`engines ${isLoading ? 'isLoading' : ''}`}>
                {motorizations.map((motorisation, index) => {
                    const getPower = () =>
                        motorisation?.characteristics?.find((char: { key: string }) => char.key === 'maximumPowerKW')
                            ?.value;

                    if (!motorisation.isAvailable) {
                        return (
                            <div key={index}>
                                <button className={`engine isDisabled`}>
                                    <div className="inner">
                                        <span className="engineTitle">
                                            <span className="icon" />
                                            {motorisation.title}
                                        </span>
                                        <span className="engineDescription">{`${motorisation.fuel}, ${
                                            motorisation.gearboxTitle
                                        } ${
                                            motorisation?.characteristics?.length >= 1
                                                ? `, ${getPower()} ${t('configurator.info.power.label')}`
                                                : ''
                                        }`}</span>
                                    </div>
                                </button>
                            </div>
                        );
                    }

                    const enginePrice = getOptionMonthlyPrice(motorisation);
                    const isSelected = motorisation.engineGearboxFuelSlug === carDetails.engineGearboxFuelSlug;

                    return (
                        <div key={index}>
                            <button
                                onClick={() => onClickEngineComparator(motorisation)}
                                className={`engine ${isSelected ? 'isSelected' : ''}`}
                                data-testId={`TESTING_ENGINE_${index}${isSelected ? '_IS_SELECTED' : ''}`}
                            >
                                <div className="inner">
                                    <span
                                        className="engineTitle"
                                        data-testId={`TESTING_ENGINE_NAME_${index}`}
                                    >
                                        <span className={`icon ${isSelected ? 'selectedBorder' : ''}`}>
                                            {isSelected && <span className="selected" />}
                                        </span>
                                        {motorisation.title}
                                    </span>
                                    <span
                                        className="engineDescription"
                                        data-testId={`TESTING_ENGINE_DESCRIPTION_${index}`}
                                    >{`${motorisation.fuel}, ${motorisation.gearboxTitle}${
                                        motorisation?.characteristics?.length >= 1
                                            ? `, ${getPower()} ${t('configurator.info.power.label')}`
                                            : ''
                                    }`}</span>
                                    <span
                                        className="enginePrice"
                                        data-testId={`TESTING_ENGINE_PRICE_${index}`}
                                    >
                                        {formatMoney(
                                            isSOLEnabled
                                                ? getTotalPriceObjByType(
                                                      motorisation.pricesV2,
                                                      IPriceV2Types.TOTAL_B2C_CASH
                                                  )?.breakdown.finalCustomerCashPriceOnTheRoad
                                                : getTotalPriceObjByType(
                                                      motorisation.pricesV2,
                                                      IPriceV2Types.TOTAL_B2C_CASH
                                                  )?.finalPriceInclTax,
                                            true
                                        )}{' '}
                                        {(enginePrice.price || enginePrice.defaultPrice) && (
                                            <span>
                                                <span className="or">{t('configurator.optionPrice.suffix')}</span>{' '}
                                                {formatMoney(enginePrice.price || enginePrice.defaultPrice, false)}{' '}
                                                <PriceSuffix isMonthlyPrice hideAsterisk />
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
